import { useEffect, useRef } from "react";
import { useMedia } from "react-use";
import { useRouter } from "next/router";
import { BagModalShell } from "@/components/tactsuit/components/buy-tactsuit/bag-modal-shell";
import { AddedToBagBody } from "@/components/tactsuit/components/buy-tactsuit/added-to-bag-content";
import { useAddedToBagStore } from "@/store/cart-store/useAddedToBagStore";
import { useTrans } from "@/utils/useTrans";
import CrossIcon from "@/v2-components/core/icon/cross-icon";

/**
 * 「담기 완료」 팝 — **GNB 장바구니 아이콘 아래**에 붙는다 (#5366).
 *
 * ## 왜 모달이 아니라 앵커 팝인가
 *
 * #5355 는 중앙 다이얼로그였다(#5359 로 머지). 기획이 정정했다 — *"팝업은 뒤에 딤처리 없이
 * 장바구니 아이콘 아래에 배치"* (이슈 #5366). **딤이 없다**는 것이 핵심이다: 담기는 흐름을
 * 끊는 사건이 아니라 알림이고, 딤을 깔면 사용자가 모달을 닫아야 계속 쇼핑할 수 있다.
 *
 * ⚠️ **「딤 없음」은 데스크톱 팝에만 해당한다.** 모바일 바텀 시트는 Radix `Sheet` 기본
 * 오버레이를 **그대로 쓴다**(기획 확인 2026-08-26). 시트는 화면을 덮으므로 딤이 바깥 탭
 * 닫기도 받아주고, 형제 시트(`retailer-sheet`·번들 병합)와 일관된다. 이 문단을 "딤은 어디서도
 * 없다" 로 읽고 시트의 오버레이를 지우지 말 것 — 아래 바깥 클릭 리스너가 **모바일에서는
 * 붙지 않는** 이유가 그 오버레이다.
 *
 * ## 기준 박스는 **가방 아이콘 래퍼**다 (기획: 아이콘 아래 20px · 우측정렬)
 *
 * `right-0` 이면 팝 우측 끝이 **아이콘 우측 끝**에 맞는다. 우측 묶음에 붙이면 그 오른쪽의
 * 아바타·국가 버튼만큼 더 오른쪽으로 밀린다(실측 1024px 에서 56px).
 *
 * 세로는 `50% + 아이콘 절반(12) + 간격(20)` = `50% + 32px`. 래퍼 높이가 아이콘 높이와
 * 같으므로 `50%` 가 곧 아이콘 중앙이다. 세 입력을 다 상수로 둔다 — 하나만 바뀌어도 테스트가
 * 먼저 깨진다(쿠폰 말풍선이 같은 방식이다, `profile-coupon-alert.tsx`).
 *
 * ⚠️ **헤더 높이 기준으로 잡지 않는다** — 헤더는 화면폭마다 64/70px 로 달라 간격이 어긋난다.
 * ⚠️ **거리 상수로 아이콘을 가리키지 않는다** — `Tracking`(로그인 시 숨김)·`LocaleButton`
 *    (laptop 이상만)이 조건부라 묶음 우측 끝에서 아이콘까지의 거리가 상태·화면폭마다 다르다.
 *
 * ## ⚠️ 이 팝은 **헤더 밖으로 돌출**하고 네비 구간을 침범한다 — 실측했다
 *
 * `docs/pitfalls.md` #32 가 *"의도된 돌출은 쿠폰 말풍선과 그 꼬리 **둘뿐**"* 이라고 적어
 * 뒀고 이 팝이 **셋째**다. 말풍선은 `w-max whitespace-nowrap` 알약인데 이 팝은 **360px
 * 카드**라 왼쪽으로 훨씬 뻗는다. 브라우저로 쟀다 (1024px × de):
 *
 *   네비 구간   x 244..770
 *   팝(아이콘 우측정렬)  x 588..948  → **182px 겹친다**
 *   팝(묶음 우측정렬)    x 644..1004 → 126px 겹친다  ← 이쪽도 안전하지 않다
 *
 * 사고 사례가 **54px** 이었으니 어느 정렬이든 그대로 두면 회귀다. 폭을 줄여 피할 수도
 * 없다 — 겹치지 않으려면 178px 이하여야 하고 카드가 안 들어간다.
 *
 * → **해결은 정렬이 아니라 `GlobalNavigationBar` 의 `onPointerEnter`** 다: 네비에 포인터가
 *   들어오면 팝을 닫는다. 패널이 열리기 전에 팝이 사라지므로 dead zone 이 생기지 않고,
 *   "이제 둘러본다"는 의도와도 맞다. 그 리스너가 이 컴포넌트의 **전제**다.
 *
 * ## #31 — `z-40` 은 여기서 힘이 없다
 *
 * "헤더 밖으로 넘치는 요소는 자기 `z-*` 가 아니라 **헤더 래퍼의 z** 로 싸운다". 아래
 * `z-40` 은 헤더 스태킹 컨텍스트(z-30/40) **안에서만** 유효하고, 바깥과의 승부는 헤더
 * 래퍼가 한다 — 올리고 내려도 네비 패널(z-[25])과의 관계는 안 바뀐다(그래서 위 침범이
 * z 로 해결되지 않는다). 말풍선(`z-40`)과 같은 값으로 두어 **둘의 관계만** 정한다.
 */
/** 장바구니 아이콘 크기 = `ShoppingBagIcon` 의 `size-6`. */
const CART_ICON_SIZE_PX = 24;
/** 기획 확정: 아이콘 **영역 아래 20px**. */
const GAP_BELOW_ICON_PX = 20;
/** 기준 박스(`h-full`) 세로 중앙에서 팝 상단까지 — 아래 `top-[calc(50%+32px)]` 와 같아야 한다. */
const POP_TOP_FROM_CENTER_PX = CART_ICON_SIZE_PX / 2 + GAP_BELOW_ICON_PX;

/**
 * 모바일 표면은 **바텀 시트**다(기획 확정 2026-08-25). 데스크톱 앵커를 좁은 폭에 그대로 두면
 * 폭 400px 이 화면을 넘고, 아이콘이 화면 우측 끝이라 팝이 잘린다.
 *
 * ⚠️ 브레이크포인트는 `BagModalShell`·`checkout-choice-dialog` 와 **같은 1023px** 이어야 한다 —
 * 갈리면 같은 화면 폭에서 팝과 시트가 동시에/둘 다 아닌 상태가 된다. 가드가 값으로 잠근다.
 */
export function AddedToBagPop() {
  const skus = useAddedToBagStore((s) => s.skus);
  const isOpen = skus.length > 0;
  const close = useAddedToBagStore((s) => s.close);
  const isMobile = useMedia("(max-width: 1023px)", false);
  const router = useRouter();
  const { t } = useTrans(["gnb"]);
  const popRef = useRef<HTMLDivElement | null>(null);

  const onViewCart = () => {
    close();
    router.push("/cart");
  };

  // 바깥 클릭·Esc 로 닫는다. 딤이 없으므로 **오버레이가 클릭을 받아주지 않는다** —
  // Radix Dialog 를 쓸 때 공짜로 얻던 거동을 여기서는 직접 붙여야 한다.
  //
  // ⚠️ `mousedown` 이다(`click` 이 아니다). 팝 안의 버튼이 라우팅하면 그 엘리먼트가 DOM 에서
  //    사라져 `click` 시점의 `contains()` 가 거짓이 되고, 바깥 클릭으로 오인한다.
  useEffect(() => {
    if (!isOpen || isMobile) return;
    const onDown = (e: MouseEvent) => {
      if (!popRef.current?.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && close();
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [isOpen, isMobile, close]);

  if (!isOpen) return null;

  if (isMobile) {
    // 셸이 시트 표면의 함정을 다 갖고 있다(`SheetHeader` 기본값 셋 · `flex flex-col` ·
    // `SheetClose` 아이콘). 여기서 다시 그리지 않는다.
    return (
      <BagModalShell open onClose={close} title={t("Added to your bag")}>
        <AddedToBagBody skus={skus} onClose={close} onViewCart={onViewCart} />
      </BagModalShell>
    );
  }

  return (
    <div
      ref={popRef}
      role="dialog"
      aria-label={t("Added to your bag")}
      // `top-[calc(50%+32px)]` = 중앙 + 아이콘 절반(12) + 간격(20). 위 상수와 같은 값이다.
      className="absolute right-0 top-[calc(50%+32px)] z-40 flex w-[360px] flex-col gap-6 rounded-lg bg-white p-6 shadow-[0px_8px_16px_0px_rgba(0,0,0,0.15)]"
    >
      <div className="flex items-center gap-3">
        <p className="min-w-0 flex-1 text-lg font-500 tracking-[0.15px] text-coolgray-90">
          {t("Added to your bag")}
        </p>
        <button
          type="button"
          aria-label={t("Close")}
          onClick={close}
          className="shrink-0 focus:outline-none"
        >
          <CrossIcon />
        </button>
      </div>
      <AddedToBagBody skus={skus} onClose={close} onViewCart={onViewCart} />
    </div>
  );
}

/** 가드가 계산을 검산할 수 있게 노출한다 — 클래스의 `32px` 와 이 값이 같아야 한다. */
export const __POP_TOP_FROM_CENTER_PX = POP_TOP_FROM_CENTER_PX;
